import * as React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PhotoPicker from '../components/PhotoPicker';
import PlaceIcon from '@mui/icons-material/Place';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { submitClockRequest } from '../lib/clockLocations';

// Field limits match the backend.
const LIMITS = { name: 256, address: 256, ward: 128, visibility: 64, clock_type: 64, surveyed_by: 256 };

const EMPTY = {
    name: '', address: '', ward: '', visibility: '', clock_type: '', surveyed_by: '', survey_date: '',
};


export default function AddClockDialog(props) {
    const { open, onClose, location, setLocation, onPickOnMap, onSubmitted } = props;
    const [fields, setFields] = React.useState(EMPTY);
    const [photos, setPhotos] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [locating, setLocating] = React.useState(false);
    const [result, setResult] = React.useState(null);

    const done = result?.severity === 'success';
    const setField = (name) => (event) => setFields({ ...fields, [name]: event.target.value });

    const reset = () => {
        setFields(EMPTY);
        setPhotos([]);
        setResult(null);
        setLocation(null);
    };
    const handleClose = () => {
        reset();
        onClose();
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) {
            setResult({ severity: 'error', message: "Your device can't share its location. Use \"Pick on map\" instead." });
            return;
        }
        setLocating(true);
        setResult(null);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocating(false);
                setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
            },
            () => {
                setLocating(false);
                setResult({ severity: 'error', message: "We couldn't get your location. Use \"Pick on map\" instead." });
            },
            { enableHighAccuracy: true, timeout: 15000 }
        );
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setResult(null);
        const response = await submitClockRequest({
            name: fields.name.trim(),
            lat: location.lat,
            lon: location.lng,
            address: fields.address.trim(),
            ward: fields.ward.trim(),
            visibility: fields.visibility.trim(),
            clock_type: fields.clock_type.trim(),
            surveyed_by: fields.surveyed_by.trim(),
            survey_date: fields.survey_date,
        }, photos);
        setSubmitting(false);
        if (response.ok) {
            setResult({
                severity: 'success',
                message: 'Thank you! Your clock has been sent for approval. You can follow it in your profile.',
            });
            if (onSubmitted) onSubmitted(response.clock);
        } else {
            setResult({ severity: 'error', message: response.message });
        }
    };

    const text = (name, label, helperText) => (
        <TextField
            id={`add-clock-${name}`}
            label={label}
            value={fields[name]}
            onChange={setField(name)}
            disabled={done || submitting}
            fullWidth
            margin="dense"
            inputProps={{ maxLength: LIMITS[name] }}
            helperText={helperText}
        />
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle className="dialogTitle">
                Request a new clock
                <IconButton onClick={handleClose} aria-label="Close dialog">
                    <CloseIcon style={{ color: '#ff334b' }} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Tell us about a clock that is missing from the map. It will appear once an admin has approved it.
                </Typography>

                <Typography variant="overline" display="block" sx={{ mt: 2 }}>
                    Where is the clock?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <PlaceIcon color={location ? 'primary' : 'disabled'} />
                    <Typography variant="body1" data-testid="clock-location" sx={{ flexGrow: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {location ? `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` : 'No location chosen yet'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" size="small" startIcon={<PlaceIcon />} onClick={onPickOnMap} disabled={done || submitting}>
                        Pick on map
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<MyLocationIcon />} onClick={useMyLocation} disabled={done || submitting || locating}>
                        {locating ? 'Locating...' : 'Use my location'}
                    </Button>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <TextField
                        id="add-clock-name"
                        label="Clock name / common name"
                        value={fields.name}
                        onChange={setField('name')}
                        disabled={done || submitting}
                        required
                        fullWidth
                        margin="dense"
                        inputProps={{ maxLength: LIMITS.name }}
                    />
                    {text('address', 'Address')}
                    {text('ward', 'Ward')}
                    {text('visibility', 'Visibility', 'How easy the clock is to see, e.g. good')}
                    {text('clock_type', 'Clock type')}
                    {text('surveyed_by', 'Surveyed by', 'Leave blank to use your name')}
                    <TextField
                        id="add-clock-survey-date"
                        label="Date of last survey"
                        type="date"
                        value={fields.survey_date}
                        onChange={setField('survey_date')}
                        disabled={done || submitting}
                        fullWidth
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        helperText="Leave blank for today"
                    />
                </Box>

                <PhotoPicker photos={photos} onChange={setPhotos} disabled={done || submitting} />
                {result ? <Alert severity={result.severity} sx={{ mt: 2 }}>{result.message}</Alert> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{done ? 'Close' : 'Cancel'}</Button>
                {done ? null : (
                    <Button onClick={handleSubmit} disabled={!fields.name.trim() || !location || submitting}>
                        Send for approval
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
